<script lang="ts" setup>
import { computed } from 'vue'

import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import TnxLink from '~/components/link/TnxLink.vue'
import LoadingTable from '~/components/loading/LoadingTable.vue'
import NoDataPreview from '~/components/preview/NoDataPreview.vue'
import CustomSuspense from '~/components/suspense/CustomSuspense.vue'
import { transactionsColumns } from '~/components/table/transactions/transactionsColumn.const'
import { ViewTransactionTable } from '~/components/table/transactions/transactionsTable.type'
import { useTableColumn } from '~/composables/useTable'
import BcLink from '~/ui/Link/BcLink.vue'
import BcTable from '~/ui/Table/BcTable.vue'

interface IProps {
  rows: ViewTransactionTable[]
  isLoading: boolean
  rowCnt: number
  hideColumns?: string[]
}

const props = withDefaults(defineProps<IProps>(), {
  rows: () => [],
  hideColumns: () => []
})

const path = useRoute().fullPath.split('/').filter(elem => elem)
const localStorageKey = path.length > 1 ? `${path[0]}-${path[path.length - 1]}` : path[0]

const hideAge = ref()

if (process.client) {
  hideAge.value = JSON.parse(window.localStorage.getItem(localStorageKey) || 'false')
}

const exceptList = computed(() => {
  const targetColumn = hideAge.value ? 'age' : 'unixTimeStamp'
  return [ targetColumn, ...props.hideColumns ]
})

const toggle = () => {
  if (process.client) {
    hideAge.value = !hideAge.value
    window.localStorage.setItem(localStorageKey, JSON.stringify(hideAge.value))
  }
}

const { column } = useTableColumn(transactionsColumns, exceptList)

</script>

<template>
  <CustomSuspense
    :is-loading="isLoading"
    :is-empty="isEmpty(rows)"
  >
    <template #loading>
      <LoadingTable
        :row-cnt="rowCnt"
        :cols="column"
      />
    </template>
    <template #empty>
      <NoDataPreview />
    </template>
    <template #default>
      <BcTable
        :cols="column"
        :rows="rows"
        @change-table-data="toggle"
      >
        <template #transactionHash="{row}: {row: ViewTransactionTable}">
          <tnx-link
            class="link"
            :hash="row.transactionHash"
          />
        </template>

        <template #blockNumber="{row}: {row: ViewTransactionTable}">
          <BcLink
            :content="row.blockNumber"
            :to="routes.getBlockDetailPageWithTab(row.blockNumber, blockDetailPageType.block)"
          />
        </template>

        <template #method="{row}: {row: ViewTransactionTable}">
          <!-- type missing -->
          <Chip
            v-if="row.method"
            v-tooltip="row.method"
            :value="row.method"
          />
        </template>

        <template #from="{row}: {row: ViewTransactionTable}">
          <AddressCopyLink
            class="link"
            :tooltip="row.from.address"
            :address="row.from.address"
            :name="row.from.name"
            :type="row.from.type"
            hide-eoa-icon
          />
        </template>

        <template #to="{row}: {row: ViewTransactionTable}">
          <AddressCopyLink
            class="link"
            :tooltip="row.to.address"
            :address="row.to.address"
            :name="row.to.name"
            :type="row.to.type"
            hide-eoa-icon
          />
        </template>

        <template #arrow-right>
          <FontAwesomeIcon icon="arrow-right" />
        </template>
      </BcTable>
    </template>
  </CustomSuspense>
</template>

<style scoped lang="scss">
.link {
  max-width: 200px;

  @include over_xl {
    max-width: 270px;
  }
}
</style>